import { Button, Checkbox, Col, Divider, Form, Input, message, Row, Select, Typography } from "antd";
import { Rule } from "antd/lib/form";
import { useForm } from "antd/lib/form/Form";
import TextArea from "antd/lib/input/TextArea";
import { UploadFile } from "antd/lib/upload/interface";
import { shopApi } from "api/shop.api";
import { CoordAddress } from "components/Map/GoogleMapAutoComplete";
import { MultiImageUpload } from "components/Upload/MultiImageUpload";
import { SingleImageUpload } from "components/Upload/SingleImageUpload";
import { Gender } from "constant";
import { observer } from "mobx-react";
import { useEffect, useMemo, useState } from "react";
import { businessTypeStore } from "store/businessTypeStore";
import { ModalStatus } from "types/modal";
import { Shop, ShopType, ShopTypeTrans } from "types/shop";
import { GoogleLocationModal as ShopChooseAddressModal } from "../ShopChooseAddressModal";
import { UserPosition } from "types/user-position";
import { userPositionApi } from "api/userPosition.api";
import { customerApi } from "api/customer.api";

const { Option } = Select;

const style = {};

interface ShopForm extends Shop {
  phone: string;
  avatar: string;
  businessTypeId: number;
  areaId: number;
  gender: Gender;
  userPositionId: number;
}

export const InfoTab = observer(
  ({
    shop,
    status,
    visible,
    onSubmit,
    onClose,
  }: {
    shop: Partial<Shop>;
    status: ModalStatus;
    visible: boolean;
    onSubmit: () => void;
    onClose: () => void;
  }) => {
    const rules: Rule[] = [{ required: true }];
    const [form] = useForm<ShopForm>();
    const [loading, setLoading] = useState(false);
    const [thumbnails, setThumbnails] = useState<string[]>([]);
    const [visibleChooseAddressModal, setVisibleChooseAddressModal] = useState(false);

    const [addressCoord, setAddressCoord] = useState<CoordAddress>({
      lat: 0,
      lng: 0,
    });
    const [userPositions, setUserPositions] = useState<Partial<UserPosition[]>>([]);

    useEffect(() => {
      fetchData();
    }, [visible, status]);

    const fetchData = async () => {
      const res = await userPositionApi.findAll({ page: 1, limit: 50 });
      setUserPositions([{ id: undefined, name: "- Không chọn -" }, ...res.data.userPositions]);
    };

    useEffect(() => {
      form.resetFields();

      if (status === "update") {
        const { customer } = shop;
        form.setFieldsValue({
          ...shop,
          businessTypeId: shop.businessType?.id,
          areaId: shop.area?.id,
          ownerName: customer?.firstName,
          // avatar: shop.avatar,
          website: shop.website,
          ownerPhone: customer?.phone,
          ownerAvatar: customer?.avatar,
          ownerEmail: customer?.email,
          ownerGender: customer?.gender,
          userPositionId: customer?.userPosition?.id,
        });
        setAddressCoord({
          lat: shop.lat || Number(import.meta.env.VITE_CENTER_LAT),
          lng: shop.long || Number(import.meta.env.VITE_CENTER_LNG),
          address: shop.address,
        });
      }
    }, [visible, shop]);

    const handleSubmitForm = async () => {
      await form.validateFields();

      try {
        setLoading(true);
        const formData = form.getFieldsValue();

        const accountData = {
          username: formData.username,
          password: formData.password,
          name: formData.ownerName,
          phone: formData.ownerPhone,
          gender: formData.ownerGender,
          dob: formData.ownerDob,
          ownerName: formData.ownerName,
          email: formData.ownerEmail,
          avatar: formData.ownerAvatar,
          userPositionId: formData.userPositionId,
        };

        const businessData = {
          name: formData.name,
          address: formData.address,
          address1: formData.address1,
          phone: formData.phone,
          // email: formData.email,
          description: formData.description,
          businessTypeId: formData.businessTypeId,
          type: formData.type,
          // avatar: formData.avatar,
          website: formData.website,
          businessArea: formData.businessArea,
          potentialCustomer: formData.potentialCustomer,
          connectionNeed: formData.connectionNeed,
          isHighlight: formData.isHighlight,
          position: formData.position,
          isDeploying: formData.isDeploying,
          isBaoChung: formData.isBaoChung,
          avatar: formData.avatar,
        };

        const businessDataPost = {
          shop: {
            ...businessData,
            long: addressCoord.lng,
            lat: addressCoord.lat,
          },
          thumbnails,
          businessTypeId: businessData.businessTypeId,
          areaId: form.getFieldValue("areaId"),
        };

        switch (status) {
          case "create": {
            try {
              await shopApi.checkUsername({
                username: form.getFieldValue("username"),
              });
            } catch (error) {
              return;
            }
            const res = await shopApi.create(businessDataPost);
            if (res) {
              await shopApi.updateAccount(res.data.id, { ...accountData });
            }
            message.success("Tạo mới thành công");
            form.resetFields();
            onClose();

            break;
          }

          case "update": {
            const { customer } = shop;
            if (!customer) {
              return;
            }
            await customerApi.update(customer.id, {
              customer,
              userPositionId: accountData.userPositionId ? accountData.userPositionId : 0, //if user clear position, the id will be 0
            });
            await shopApi.update(shop?.id || 0, businessDataPost);
            await shopApi.updateAccount(shop?.id || 0, { ...accountData });

            message.success("Cập nhật thành công");
            break;
          }
        }

        onSubmit();
      } finally {
        setLoading(false);
      }
    };

    const imageList = useMemo<UploadFile<any>[]>(() => {
      if (shop.thumbnails) {
        setThumbnails(shop.thumbnails.map((e) => e.url));
        return shop.thumbnails?.map((e) => ({
          name: e.id + "",
          url: e.url,
          uid: e.id + "",
        }));
      }
      setThumbnails([]);
      return [];
    }, [shop.thumbnails]);

    return (
      <Form
        layout="vertical"
        onFinish={handleSubmitForm}
        initialValues={{
          isHighlight: false,
          isDeploying: false,
          isBaoChung: false,
        }}
        form={form}
      >
        <Row gutter={32}>
          <Col span={12} style={{ padding: "0 2em", borderRight: "1px solid #efefef" }}>
            <Row gutter={12}>
              <Col span={24}>
                <Divider orientation="left" orientationMargin="0">
                  <Typography.Title level={3}>Thông tin doanh nghiệp</Typography.Title>
                </Divider>
              </Col>
              <Col span={24}>
                <Form.Item name="position" label="Số thứ tự">
                  <Input type="number" />
                </Form.Item>
                {/* <Form.Item shouldUpdate={true}>
                  {() => {
                    return (
                      <Form.Item
                        label="Ảnh doanh nghiệp"
                        name="avatar"
                        style={{ marginBottom: 0 }}
                      >
                        <SingleImageUpload
                          width={102}
                          height={102}
                          onUploadOk={(path: string) => {
                            form.setFieldsValue({
                              avatar: path,
                            });
                          }}
                          imageUrl={form.getFieldValue("avatar")}
                        />
                      </Form.Item>
                    );
                  }}
                </Form.Item> */}
              </Col>
              <Col span={8}>
                <Form.Item name="isHighlight" valuePropName="checked">
                  <Checkbox>Đánh dấu nổi bật</Checkbox>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="isDeploying" valuePropName="checked">
                  <Checkbox>Là ban triển khai</Checkbox>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="isBaoChung" valuePropName="checked">
                  <Checkbox>Được bảo chứng</Checkbox>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label="Tên doanh nghiệp" name="name" rules={rules}>
                  <Input placeholder="" />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item label="Địa chỉ" rules={rules}>
                  <Form.Item noStyle name="address">
                    <Input style={{ paddingRight: 105 }} readOnly placeholder="" />
                  </Form.Item>
                  <Button
                    type="link"
                    style={{ position: "absolute", right: 0 }}
                    onClick={() => setVisibleChooseAddressModal(true)}
                  >
                    Chọn địa chỉ
                  </Button>
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item label="Địa chỉ cụ thể (Số nhà, tòa nhà, tầng, lầu)" name="address1">
                  <Input placeholder="" />
                </Form.Item>
              </Col>

              {/* <Col span={12}>
                <Form.Item
                  style={style}
                  label="Số điện thoại"
                  name="phone"
                  rules={rules}
                >
                  <Input placeholder="" />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  style={style}
                  label="Email"
                  name="email"
                  // rules={rules}
                >
                  <Input placeholder="" />
                </Form.Item>
              </Col> */}

              <Col span={12}>
                <Form.Item
                  style={style}
                  label="Loại hình kinh doanh"
                  name="businessTypeId"
                  // rules={rules}
                >
                  <Select style={{ width: "100%" }}>
                    {businessTypeStore.list.map((e) => (
                      <Option key={e.id} value={e.id}>
                        {e.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  style={style}
                  label="Lĩnh vực kinh doanh"
                  name="businessArea"
                  // rules={rules}
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  style={style}
                  label="Website"
                  name="website"
                  // rules={rules}
                >
                  <Input placeholder="" />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  style={style}
                  label="Số điện thoại"
                  name="phone"
                  // rules={rules}
                >
                  <Input placeholder="" />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item style={style} label="Giới thiệu về doanh nghiệp" name="description">
                  <TextArea rows={5} />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item shouldUpdate={true}>
                  {() => {
                    return (
                      <Form.Item label="Ảnh đại diện doanh nghiệp" name="avatar" style={{ marginBottom: 0 }}>
                        <SingleImageUpload
                          width={102}
                          height={102}
                          onUploadOk={(path: string) => {
                            form.setFieldsValue({
                              avatar: path,
                            });
                          }}
                          imageUrl={form.getFieldValue("avatar")}
                        />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </Col>

              {/* <Col span={12}>
                <Form.Item style={style} label="Loại doanh nghiệp">
                  <Form.Item name="type" noStyle>
                    <Select style={{ width: "100%" }}>
                      {Object.values(sảnType).map((type) => (
                        <Option key={type} value={type}>
                          {ShopTypeTrans[type]}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item noStyle shouldUpdate>
                    {() => (
                      <>
                        {form.getFieldValue("type") != ShopType.Company && (
                          <Form.Item
                            name="isAllowOrder"
                            valuePropName="checked"
                            noStyle
                          >
                            <Checkbox>Cho phép đặt đơn</Checkbox>
                          </Form.Item>
                        )}
                      </>
                    )}
                  </Form.Item>
                </Form.Item>
              </Col> */}

              {/* <Col span={24}>
                <Form.Item shouldUpdate noStyle>
                  {() => (
                    <>
                      {form.getFieldValue("type") == ShopType.Retail && (
                        <Form.Item
                          name={"shipContent"}
                          label="Ghi chú giao hàng"
                        >
                          <TextArea rows={3} />
                        </Form.Item>
                      )}
                    </>
                  )}
                </Form.Item>
              </Col> */}
            </Row>
          </Col>

          <Col span={12} style={{ padding: "0 2em" }}>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item style={style} label="Top sản phẩm" name="potentialCustomer">
                  <TextArea rows={3} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  style={style}
                  // label="Đối tác referral lý tưởng"
                  label="Khách hàng/Đối tác tiềm năng"
                  name="connectionNeed"
                >
                  <TextArea rows={3} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item shouldUpdate={true}>
                  {() => {
                    return (
                      <Form.Item
                        label="Ảnh khác"
                        tooltip={
                          "Những ảnh này sẽ được hiển thị khi người dùng truy cập vào doanh nghiệp của bạn"
                        }
                        name="thumbnails"
                        style={{ marginBottom: 0 }}
                      >
                        <MultiImageUpload
                          onUploadOk={(fileList) => {
                            setThumbnails(fileList);
                          }}
                          fileListProp={imageList}
                        />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </Col>

              <Col span={24}>
                <Divider orientation="left" orientationMargin="0">
                  <Typography.Title level={3}>Thông tin thành viên</Typography.Title>
                </Divider>
              </Col>
              <Col span={24}>
                <Form.Item shouldUpdate={true}>
                  {() => {
                    return (
                      <Form.Item label="Avatar" name="ownerAvatar" style={{ marginBottom: 0 }}>
                        <SingleImageUpload
                          width={102}
                          height={102}
                          onUploadOk={(path: string) => {
                            form.setFieldsValue({
                              ownerAvatar: path,
                            });
                          }}
                          //TODO: show avatar lúc cập nhật
                          imageUrl={form.getFieldValue("ownerAvatar")}
                        />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Tên tài khoản" name="username" rules={rules}>
                  <Input autoComplete="new-username" />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Mật khẩu"
                  name="password"
                  tooltip={{
                    title: "Để trống sẽ không cập nhật",
                  }}
                  style={{ marginBottom: 8 }}
                >
                  <Input.Password type="password" placeholder="" autoComplete="new-password" />
                </Form.Item>
              </Col>

              <Col span={24} />

              <>
                <Col span={12}>
                  <Form.Item label="Họ tên" name={"ownerName"} rules={rules}>
                    <Input placeholder="" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Giới tính" name={"ownerGender"} rules={rules}>
                    <Select>
                      <Select.Option value={Gender.Male}>Nam</Select.Option>
                      <Select.Option value={Gender.FeMale}>Nữ</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Số điện thoại" name={"ownerPhone"} rules={rules}>
                    <Input />
                  </Form.Item>
                </Col>
                {/* <Col span={12}>
                  <Form.Item label="Ngày sinh" name={"ownerDob"}>
                    <DatePicker
                      format={"DD/MM/YYYY"}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Giới tính" name={"gender"}>
                    <Select style={{ width: "100%" }}>
                      <Select.Option value={Gender.Male}>Nam</Select.Option>
                      <Select.Option value={Gender.FeMale}>Nữ</Select.Option>
                    </Select>
                  </Form.Item>
                </Col> */}
                {/* <Col span={12}>
                  <Form.Item label="Quê quán">
                    <Input placeholder="" />
                  </Form.Item>
                </Col> */}

                <Col span={12}>
                  <Form.Item label="Email" name={"ownerEmail"}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Chức vụ" name={"userPositionId"}>
                    <Select allowClear>
                      {userPositions.map((item: any) => (
                        <Select.Option value={item.id}>{item.name}</Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </>
            </Row>
            <Form.Item style={{ float: "right", marginBottom: 0 }}>
              <Button type="primary" htmlType="submit" loading={loading}>
                {status === "create" ? "Tạo mới" : "Cập nhật"}
              </Button>
            </Form.Item>
          </Col>
        </Row>

        <ShopChooseAddressModal
          visible={visibleChooseAddressModal}
          onClose={() => {
            setVisibleChooseAddressModal(false);
          }}
          onSubmitOk={(coord) => {
            setAddressCoord(coord);
            form.setFieldsValue({
              address: coord.address,
            });
          }}
          coord={addressCoord}
        />
      </Form>
    );
  },
);
